/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import { ListItem, ListItemButton, ListItemText, styled, Typography } from '@mui/material';
import { Box } from '@mui/system';
import type { Feature, Polygon } from 'geojson';
import React from 'react';
import { useMemo } from 'react';
import { List, type RowComponentProps } from 'react-window';
import { useAtomValue, useSetAtom } from 'jotai';
import { loadable } from '../utils/loadable';
import { lookupEventColor, selectedAlertAtom } from './IPAWSLayer';

import { ipawsChunksSelector } from './selectors';
import { Link, useSearchParams } from 'react-router-dom';
import { nifcDataChunksAtom } from '../NIFC/selectors';
import { NifcPerimeterProperties } from '../NIFC/types';
import { canopyChunksSelector } from '../canopy/selectors';
import { groupBy } from 'lodash';
import { cameraFlightAtom } from '../map/flyToFeature';
import { bbox, featureCollection } from '@turf/turf';
import { wowChunksSelector } from '../wow/selectors';
import { WowProperties } from '../wow/types';
import { POSTProperties } from '../Post/types';
import { lookupCategoryColor } from '../wow/WowLayer';
import { selectedFeatureAtom } from '../Incident/FeaturePopup';
import { selectEventSnapshotAtom } from '../map/selectEventSnapshot';
import { Source } from '@mui/icons-material';

type Props = {
  selectedMap: 'ipaws' | 'nifc' | 'wow' | 'canopy';
  setSelectedMap: () => void;
};

const ROW_HEIGHT = 66;
const OVERSCAN_COUNT = 5;
const LIST_SIZE = { height: 300, width: 250 };

/**
 * The properties the IPAWS and NIFC rows read. Both feeds serve a superset of
 * these, and neither serves all of them, so every field is optional.
 */
type AlertProperties = {
  area?: string;
  event?: string;
  priority_rank?: number;
  incidentname?: string;
  county?: string;
  event_name?: string;
  createdate?: string;
};

export const AlertsList = React.memo((props: Props) => {
  // The 24/7 Monitoring lists only exist on the home dashboard, which is the
  // one view that displays these products.
  const wowData = useAtomValue(loadable(wowChunksSelector({ type: 'perimeter', enabled: true })));

  // The chunk selector serves every WoW feed under one loose feature type, so
  // the perimeter columns are named here the same way WowLayer names them.
  const wowAlerts = (wowData.state === 'hasData' ? wowData.data : [])
    .map(chunk => chunk.features.map(feature => feature))
    .flat()
    .sort((a, b) => (a.properties?.Shape__Area < b.properties?.Shape__Area ? 1 : -1)) as Feature<
    Polygon,
    WowProperties
  >[];

  // IPAWS
  const ipawsData = useAtomValue(loadable(ipawsChunksSelector(true)));
  const alertListData = useMemo(() => {
    if (ipawsData.state !== 'hasData') {
      return [];
    }
    const ipawsFeatures = ipawsData.data
      .map(chunk => chunk.features.map(feature => feature))
      .flat();

    const alertsListData = [...(ipawsFeatures ?? [])].sort((a, b) =>
      (a.properties?.event ?? '') > (b.properties?.event ?? '') ? 1 : -1
    );

    return alertsListData;
  }, [ipawsData]);

  // NIFC
  const nifcPerimeters = useAtomValue(nifcDataChunksAtom<NifcPerimeterProperties>('perimeter'));
  const nifcAlerts = nifcPerimeters
    .map(chunk => chunk.features.map(feature => feature))
    .flat()
    .sort((a, b) => (a.properties.run_time < b.properties.run_time ? 1 : -1));

  const canopyData = useAtomValue(loadable(canopyChunksSelector));
  const canopyListData = useMemo(() => {
    if (canopyData?.state !== 'hasData') {
      return [];
    }

    const canopyFeatures = canopyData?.data
      .map(chunk => chunk.features.map(feature => feature))
      .flat();

    const canopyDataList = Object.values(groupBy(canopyFeatures, 'properties.kml_id'));

    return canopyDataList || [];
  }, [canopyData]);

  const alerts: Feature<Polygon, AlertProperties>[] =
    props.selectedMap === 'ipaws' ? alertListData : nifcAlerts;

  // Each list is paired with the row component that understands its items, so
  // the count a list renders against and the array its rows index into are the
  // same array -- a count read from a separately derived value could outlive the
  // data it describes. The rows still guard the entry they read: the arrays are
  // rebuilt from feeds that are replaced wholesale, so an index can stop
  // resolving between renders even when the pairing is right.
  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 300,
      }}
    >
      <Box sx={{ width: '100%' }}>
        {props.selectedMap === 'canopy' && (
          <List
            rowComponent={CanopyRow}
            rowProps={{ setSelectedMap: props.setSelectedMap, items: canopyListData }}
            rowCount={canopyListData.length}
            rowHeight={ROW_HEIGHT}
            overscanCount={OVERSCAN_COUNT}
            style={LIST_SIZE}
          />
        )}
        {props.selectedMap === 'wow' && (
          <List
            rowComponent={WowRow}
            rowProps={{ setSelectedMap: props.setSelectedMap, items: wowAlerts }}
            rowCount={wowAlerts.length}
            rowHeight={ROW_HEIGHT}
            overscanCount={OVERSCAN_COUNT}
            style={LIST_SIZE}
          />
        )}
        {(props.selectedMap === 'ipaws' || props.selectedMap === 'nifc') && (
          <List
            rowComponent={Row}
            rowProps={{
              setSelectedMap: props.setSelectedMap,
              mapType: props.selectedMap,
              items: alerts,
            }}
            rowCount={alerts.length}
            rowHeight={ROW_HEIGHT}
            overscanCount={OVERSCAN_COUNT}
            style={LIST_SIZE}
          />
        )}
      </Box>
    </Box>
  );
});

function CanopyRow({
  index,
  style,
  ariaAttributes,
  items,
  setSelectedMap,
}: RowComponentProps<{
  setSelectedMap: () => void;
  items: Feature<Polygon, POSTProperties>[][];
}>) {
  const flyTo = useSetAtom(cameraFlightAtom);
  const selectEventSnapshot = useSetAtom(selectEventSnapshotAtom);
  const searchParams = useSearchParams();
  const features = items[index];
  // Two shapes of the same non-event: the virtualizer asked for an index the
  // current group array no longer reaches, or the group at that index holds no
  // swath. Either way the row has nothing to describe, and the next render
  // brings the count and the groups back into step. The hooks above run first
  // so a row that regains its group later still calls them in the same order.
  const feature = features?.[0];
  if (feature == null) {
    return null;
  }
  const { ef_rank, event_name, county, kml_id } = feature.properties;
  const eventType = `tornado_rank_${ef_rank}`;

  return (
    <ListItem
      style={style}
      {...ariaAttributes}
      component="div"
      disablePadding
      disableGutters
      sx={{
        color: '#565c65',
        overflow: 'hidden',
        borderLeft: `6px solid rgba(${borderColor(eventType)}, 0.7)`,
        borderBottom: '1px solid #cfe2f0',
        '&:hover': {
          color: '#005288',
          backgroundColor: 'rgb(233, 240, 245)',
          borderLeft: `6px solid rgb(${borderColor(eventType)})`,
        },
      }}
    >
      <ListItemButton
        sx={{ overflow: 'hidden', paddingTop: 2, paddingBottom: 2 }}
        onClick={() => {
          void selectEventSnapshot('canopy', feature.properties);
          setSelectedMap();
          searchParams[1]({
            canopy_kml_id: kml_id,
          });
          flyTo({ bounds: bbox(featureCollection(features)) });
        }}
      >
        <ListItemText
          slotProps={{
            primary: {
              sx: {
                display: 'flex',
                flexDirection: 'column',
              },
            },
          }}
        >
          {county !== null && <Typography variant="caption">{county}</Typography>}
          <span
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {event_name}
          </span>
        </ListItemText>
      </ListItemButton>
    </ListItem>
  );
}

function Row({
  index,
  style,
  ariaAttributes,
  items,
  mapType,
  setSelectedMap,
}: RowComponentProps<{
  setSelectedMap: () => void;
  mapType: 'ipaws' | 'nifc';
  items: Feature<Polygon, AlertProperties>[];
}>) {
  const flyTo = useSetAtom(cameraFlightAtom);
  const selectEventSnapshot = useSetAtom(selectEventSnapshotAtom);
  const setSelectedAlert = useSetAtom(selectedAlertAtom);

  // These lists are backed by 15-minute feeds that are replaced wholesale, so
  // an index the current snapshot no longer reaches has nothing to draw. The
  // hooks above run first: a row whose entry comes back on a later render must
  // still call the same hooks in the same order. The one entry read here is
  // also what the click handler acts on, so the render and the click can never
  // disagree about which feature this row is.
  const feature = items[index];
  if (feature == null) {
    return null;
  }

  const { area, event, priority_rank, county, event_name, incidentname, createdate } =
    feature.properties;

  const eventType = priority_rank ? `tornado_rank_${priority_rank}` : (event ?? 'nifc');

  return (
    <ListItem
      style={style}
      {...ariaAttributes}
      component="div"
      disablePadding
      disableGutters
      sx={{
        color: '#565c65',
        overflow: 'hidden',
        borderLeft: `6px solid rgba(${borderColor(eventType)}, 0.7)`,
        borderBottom: '1px solid #cfe2f0',
        '&:hover': {
          color: '#005288',
          backgroundColor: 'rgb(233, 240, 245)',
          borderLeft: `6px solid rgb(${borderColor(eventType)})`,
        },
      }}
    >
      <ListItemButton
        sx={{ overflow: 'hidden', paddingTop: 2, paddingBottom: 2 }}
        onClick={() => {
          if (feature.properties.event != null) {
            setSelectedAlert(feature);
          }
          // IPAWS keeps no history, so it has no snapshot to navigate to.
          if (mapType !== 'ipaws') {
            void selectEventSnapshot(mapType, feature.properties);
          }
          setSelectedMap();

          flyTo({ bounds: bbox(feature) });
        }}
      >
        <ListItemText
          slotProps={{
            primary: {
              sx: {
                display: 'flex',
                flexDirection: 'column',
              },
            },
          }}
        >
          {event != null && <Typography variant="caption">{event}</Typography>}
          {county !== null && <Typography variant="caption">{county}</Typography>}
          <span
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {area ?? incidentname ?? event_name}
          </span>
          {createdate != null && (
            <Typography variant="caption">{new Date(createdate).toLocaleDateString()}</Typography>
          )}
        </ListItemText>
      </ListItemButton>
    </ListItem>
  );
}

function WowRow({
  index,
  style,
  ariaAttributes,
  items,
  setSelectedMap,
}: RowComponentProps<{
  setSelectedMap: () => void;
  items: Feature<Polygon, WowProperties>[];
}>) {
  const flyTo = useSetAtom(cameraFlightAtom);
  const selectEventSnapshot = useSetAtom(selectEventSnapshotAtom);
  const setSelectedFeature = useSetAtom(selectedFeatureAtom);

  // Same contract as the IPAWS/NIFC row above: guard the entry, below the
  // hooks, and let the render and the click read that one entry.
  const feature = items[index];
  if (feature == null) {
    return null;
  }

  const { threat, threatlevel, categoryid, datasource } = feature.properties;

  return (
    <ListItem
      style={style}
      {...ariaAttributes}
      component="div"
      disablePadding
      disableGutters
      sx={{
        color: '#565c65',
        overflow: 'hidden',
        borderLeft: `6px solid rgba(${borderColor(categoryid)}, 0.7)`,
        borderBottom: '1px solid #cfe2f0',
        '&:hover': {
          color: '#005288',
          backgroundColor: 'rgb(233, 240, 245)',
          borderLeft: `6px solid rgb(${borderColor(categoryid)})`,
        },
      }}
    >
      <ListItemButton
        sx={{ overflow: 'hidden', paddingTop: 2, paddingBottom: 2 }}
        onClick={() => {
          if (feature.properties.threat != null) {
            setSelectedFeature({
              ...feature,
              section: 'wow-perimeter',
            });
          }
          void selectEventSnapshot('wow', feature.properties);
          setSelectedMap();

          flyTo({ bounds: bbox(feature) });
        }}
      >
        <ListItemText
          slotProps={{
            primary: {
              sx: {
                display: 'flex',
                flexDirection: 'column',
              },
            },
          }}
        >
          {threat != null && (
            <Typography
              variant="caption"
              sx={{
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
                overflow: 'hidden',
                fontWeight: 'bold',
              }}
            >
              {`${threat}: ${threatlevel}`}
            </Typography>
          )}
          {datasource != null && (
            <Typography
              variant="caption"
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Source sx={{ mr: 1, width: 16 }} /> {datasource}
            </Typography>
          )}
        </ListItemText>
      </ListItemButton>
    </ListItem>
  );
}

// Both palettes are consulted because this colours the IPAWS/NIFC rows (event
// names) and the WoW rows (category ids); each lookup is own-property guarded at
// its source, so a feed value like `constructor` cannot resolve up the prototype
// chain and interpolate a function body into the CSS template.
const borderColor = (event: string) => {
  const color = lookupEventColor(event) ?? lookupCategoryColor(event);
  // Channel triplet, not a hex literal: both call sites interpolate this into
  // rgb()/rgba(), where '#000' is invalid and drops the border entirely. NWS
  // publishes more event values than the IPAWS palette has keys, so a miss in
  // both palettes is routine.
  return color ? color.join(',') : '0,0,0';
};

type RowProps = {
  event: string;
  incidentId: string;
};

export const IncidentRow = React.memo((props: RowProps) => {
  return (
    <>
      <ListItem
        component="div"
        disablePadding
        sx={{
          color: '#565c65',
          overflow: 'hidden',
          borderBottom: '1px solid #cfe2f0',
          '&:hover': {
            color: '#005288',
            backgroundColor: '#E7F0F6',
          },
        }}
      >
        <StyledLink to={`/${encodeURIComponent(props.incidentId)}/population/map`}>
          <ListItemButton sx={{ overflow: 'hidden', paddingTop: 2, paddingBottom: 2 }}>
            <ListItemText
              slotProps={{
                primary: {
                  sx: {
                    display: 'flex',
                    flexDirection: 'column',
                  },
                },
              }}
            >
              <span
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {props.event}
              </span>
            </ListItemText>
          </ListItemButton>
        </StyledLink>
      </ListItem>
    </>
  );
});

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  width: 100%;
`;
